import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Phone } from "lucide-react";
import { PHONE, TEL } from "./data";
import { Eyebrow, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OPTIONS = ["Furniture", "Appliances", "Mattress", "Hot Tub", "Treadmill / Exercise Equipment", "Yard Waste", "Construction Debris", "Cleanout", "Other"];
const EMPTY = { name: "", phone: "", zip: "", service: "", notes: "" };
const input = "w-full rounded-xl border-2 border-[#0D0D0B]/15 bg-white px-4 py-3.5 text-base text-[#0D0D0B] placeholder:text-[#6b6b63] outline-none transition-[border-color,box-shadow] focus:border-[#0D0D0B] focus:shadow-[0_0_0_4px_rgba(255,176,0,0.45)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.18em] text-[#0D0D0B]">{label}</span>
    {children}
  </label>
);

const QuoteForm = ({ defaultService = "" }) => {
  const [f, setF] = useState({ ...EMPTY, service: defaultService });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.service) return toast.error("Pick what you need hauled.");
    setBusy(true);
    try {
      await axios.post(`${API}/quotes`, f);
      setDone(true);
      setF(EMPTY);
      toast.success("Got it! We'll call you back shortly.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace("Value error, ", "") : d || `Couldn't send. Please call ${PHONE}.`);
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <div data-testid="quote-form-success" className="flex h-full flex-col items-start justify-center gap-4 py-10">
      <CheckCircle2 className="h-14 w-14 text-[#0D0D0B]" />
      <h3 className="font-display text-5xl uppercase leading-none text-[#0D0D0B]">Request sent.</h3>
      <p className="text-[#3a3a35]">We'll call you back soon. Need it faster? Call <a href={TEL} className="font-bold underline">{PHONE}</a>.</p>
      <button data-testid="quote-form-again" onClick={() => setDone(false)} className="font-bold text-[#0D0D0B] underline">Send another</button>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="quote-form" className="grid gap-4 sm:grid-cols-2">
      <Field label="Name"><input data-testid="quote-form-name" required minLength={2} value={f.name} onChange={set("name")} className={input} placeholder="Your name" autoComplete="name" /></Field>
      <Field label="Phone"><input data-testid="quote-form-phone" required type="tel" value={f.phone} onChange={set("phone")} className={input} placeholder="(864) 555-0123" autoComplete="tel" /></Field>
      <Field label="ZIP code"><input data-testid="quote-form-zip" value={f.zip} onChange={set("zip")} inputMode="numeric" maxLength={10} className={input} placeholder="29601" autoComplete="postal-code" /></Field>
      <Field label="What needs to go?">
        <select data-testid="quote-form-service" value={f.service} onChange={set("service")} className={`${input} appearance-none`}>
          <option value="">Choose one</option>
          {OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Details (optional)"><textarea data-testid="quote-form-notes" rows={3} maxLength={1000} value={f.notes} onChange={set("notes")} className={input} placeholder="e.g. old sofa and fridge in the garage" /></Field>
      </div>
      <motion.button whileTap={{ scale: 0.98 }} data-testid="quote-form-submit-button" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#0D0D0B] py-4 text-lg font-extrabold text-[#F2EEE6] transition-colors hover:bg-[#2a2a24] disabled:opacity-60 sm:col-span-2">
        {busy && <Loader2 className="h-5 w-5 animate-spin" />} Request My Free Quote
      </motion.button>
    </form>
  );
};

export const Quote = ({ defaultService }) => (
  <section id="quote" data-testid="quote-section" className="bg-[#0D0D0B] py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <div className="flex h-full flex-col justify-between rounded-3xl border border-white/15 p-8 sm:p-10">
          <div>
            <Eyebrow>Contact</Eyebrow>
            <h2 className="font-display mt-4 text-6xl uppercase leading-[0.9] text-[#F2EEE6] sm:text-7xl">Fastest way?<br /><span className="text-[#FFB000]">Just call.</span></h2>
            <p className="mt-5 text-base text-[#d6d2c8] sm:text-lg">Talk to a real person, get your free onsite quote booked, and ask about same-day pickup.</p>
          </div>
          <motion.a whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} data-testid="quote-tap-to-call-button" href={TEL} className="cta-glow mt-10 flex items-center gap-4 rounded-2xl bg-[#FFB000] p-5 text-[#0D0D0B]">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#0D0D0B] text-[#FFB000]"><Phone className="h-6 w-6" strokeWidth={2.75} /></span>
            <span>
              <span className="block text-xs font-extrabold uppercase tracking-[0.2em]">Tap to call</span>
              <span className="font-display block text-4xl leading-none tabular-nums sm:text-5xl">{PHONE}</span>
            </span>
          </motion.a>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="h-full rounded-3xl bg-[#F2EEE6] p-6 sm:p-10">
          <h3 className="font-display mb-6 text-5xl uppercase leading-none text-[#0D0D0B] sm:text-6xl">Request a Quote</h3>
          <QuoteForm key={defaultService || "none"} defaultService={defaultService} />
        </div>
      </Reveal>
    </div>
  </section>
);
